import gql from 'graphql-tag'

import { getStripe } from '@/shared/services/stripe'
import { apolloClient } from '@/shared/graphql/client'
import {
  BillingQueryDocument,
  PlanStatus,
  UserPermission,
  type BillingQueryQuery,
  type User,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import { useWebviewStore } from '@/shared/stores/webview'
import { ToastType, useToastStore } from '@/shared/stores/toast'

export const usePaymentAuthentication = () => {
  const userStore = useUserStore()
  const billingStore = useBillingStore()
  const webviewStore = useWebviewStore()
  const toastStore = useToastStore()

  const authenticatePayment = async () => {
    if (webviewStore.isActive) {
      return false
    }

    const stripe = await getStripe()

    if (
      !stripe ||
      !billingStore.billing?.paymentMethod?.requiresAction?.clientSecret
    ) {
      return false
    }

    const { error } = await stripe.confirmCardPayment(
      billingStore.billing?.paymentMethod.requiresAction.clientSecret
    )

    if (error) {
      console.log(error)

      toastStore.showToast({
        type: ToastType.Error,
        content: 'Payment authentication failed',
        error: new Error(error.message),
      })
      return true
    }

    if (!userStore.user) return true

    apolloClient.cache.updateFragment<User>(
      {
        id: apolloClient.cache.identify(userStore.user),
        fragment: gql`
          fragment UpdateUser on User {
            permissions
          }
        `,
      },
      (response) => {
        if (!response) return

        return {
          permissions: userStore.user?.permissions.concat(UserPermission.Pro),
        } as User
      }
    )

    apolloClient.cache.updateQuery<BillingQueryQuery>(
      {
        query: BillingQueryDocument,
      },
      (response) => {
        if (!response) return

        return {
          ...response,
          billing: {
            ...response.billing,
            plan: {
              ...response.billing.plan,
              status: PlanStatus.Active,
              dateInfo: {
                __typename: 'BillingPlanDateInfo',
                date: response.billing.upcomingDetails.upcomingBilling?.date,
                prefix: 'Renews on',
              },
            },
            billingAlert: null,
          },
        } as BillingQueryQuery
      }
    )

    return true
  }

  return { authenticatePayment }
}
